// ============================================================
// PlateVehicle - a vehicle's plate at rest (plan body-map P6; the vehicle studies): the tank, skimmer and bike from
// above, the walker from its front. Each location (Hull, Turret, Motive, Rear) is the same line art under its own
// clip, recoloured by its damage; a callout in the margin names it with its tags only (no roll range); each facing's
// armour sits at that side of the vehicle, the facing in play lit; the structure runs underneath. Its conditions
// change its own lines, each on a layer of its own (fx/vehicleConditions, plan S2).
//
// It keeps the old map's names: `vehicle body hit location map`, the `<path style="cursor: context-menu">` per
// location for the armour edit, `map-zone-<loc>` sights.
// ============================================================

import { memo, useMemo, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { plateBand } from '../../engine/plate/plateBand';
import { PLATE_TYPE_VARS } from '../../engine/plate/plateType';
import { damageOf } from '../../engine/plate/plateModel';
import {
  VEHICLE_UNPLACED,
  vehicleLayout,
  vehicleStatus,
  wordsFit,
  type CalloutLayout,
} from '../../engine/plate/calloutLayout';
import { useCellSize } from './usePlateLayout';
import { Lens, useLens } from './PlateLens';
import { getBodyConfig } from '../../engine/bodyConfig';
import type { Impact } from '../../engine/impacts';
import { struckSide, vehicleThrowAt, waveAt, type VehicleSide } from '../../engine/plate/hits';
import { firstLanded, thrower, type Burst } from '../../engine/plate/burst';
import clsx from '../../utils/clsx';
import { needsClock, type PlateState } from '../../engine/plate/plateState';
import type { VehicleArt, VehicleLocation } from '../../engine/plate/vehicleArt';
import {
  piecesOf,
  vehicleCallouts,
  vehicleFacings,
  type FacingLabel,
  type VehicleCallout,
} from '../../engine/plate/vehicleModel';
import { effectiveVehicleFacing } from '../../engine/vehicleFacing';
import type { VehicleKind } from '../../engine/vehicleKind';
import type { Target } from '../../types';
import usePlateState from './usePlateState';
import styles from '../../styles/BodyPlate.module.css';
import {
  ArtDefs,
  CalloutLeader,
  scaleAbout,
  Glow,
  hitOrder,
  StatusLine,
  LocationClips,
  marksClass,
  Snow,
  SnowDefs,
  GlowHole,
  holesOf,
  PlateHit,
  PlateLines,
  PlateSights,
  plateSize,
  lockedOn,
  dimmedBy,
  type Targeting,
  ScanUnavailable,
  usePlateUid,
  vehicleRegions,
  viewBoxOf,
} from './plateParts';
import { useVehicleArt } from './usePlateArt';
import usePlateClock from './usePlateClock';
import {
  throwStyle,
  VehicleConditionDefs,
  VehicleExtras,
  VehicleLayers,
  vehicleLocationLayer,
  VehicleOverlays,
} from './fx/vehicleConditions';
import fx from '../../styles/PlateFx.module.css';
import useHoldsStill from '../../hooks/useHoldsStill';
import usePlateHits, { type HitPlay } from './usePlateHits';
import { flareClass, HitDefs, hitNodes, Struck, WaveBands, waveNodes } from './fx/hits';
import { BurstMarks } from './fx/burst';
import { StrikeMoments } from './fx/moments';
import { artFrame } from '../../engine/plate/frame';
import { guardOf, type Guard } from '../../engine/plate/moments';
import { applyScene, useSceneWriter, writeScene } from './fx/scene';

/** A vehicle's locations, as the impacts name them. */
const LOCATIONS = getBodyConfig('vehicle').locations.map((l) => l.id);

/**
 * A vehicle's hits are lighter than a person's (the vehicle studies' numbers): its lines are larger and longer, so the
 * same push reads heavier. The location's own move (CSS `--hit-k`) and the filter's pushes.
 */
const HIT_K = { lines: 0.6, filter: 0.75 };

interface Props {
  kind: VehicleKind;
  target: Target;
  side?: 'target' | 'actor';
  fill?: boolean;
  targeting?: Targeting | null;
  onLocationContextMenu?: (id: string) => void;
  /** What just happened to it (engine/impacts): a new one plays once (plan body-map H1). */
  impact?: Impact | null;
}

export default function PlateVehicle({ kind, ...rest }: Props) {
  const { art, failed } = useVehicleArt(kind);
  if (failed) return <ScanUnavailable ariaLabel="vehicle body hit location map" className={plateSize(rest.fill)} />;
  return <VehicleSvg art={art} kind={kind} {...rest} />;
}

type SvgProps = Props & { art: VehicleArt | null };

function VehicleSvg({ art, kind, target, side = 'target', fill = false, impact, ...interaction }: SvgProps) {
  const u = usePlateUid('vehicle');
  const { state, dataStates, glow } = usePlateState(target.conditions);
  const wreck = state.dead || state.explodes;
  const ref = useRef<SVGSVGElement>(null);
  const hits = usePlateHits(impact, LOCATIONS);
  const struck = useStruckSide(hits.play, effectiveVehicleFacing(target));
  const frame = useVehicleHitFrame(hits, art, struck);
  usePlateClock(ref, art !== null && (needsClock(state) || hits.clocked), frame);
  const { callouts, labels, layout } = useVehicleLayout(ref, art, target, { fill, status: wreck || state.disabled });
  const lens = useLens(layout.frame);
  // Held across renders, so the lens - the plate's state, moving with every pointer move - redraws only itself.
  const guard = useMemo(() => guardOf(target), [target]);
  const facingStruck = useFacingStruck(hits.play, struck);
  return (
    <svg
      ref={ref}
      viewBox={viewBoxOf(layout.frame)}
      className={clsx(plateSize(fill), styles.plate, side === 'actor' && styles.actor, wreck && styles.dead)}
      data-band={plateBand(target, !!wreck)}
      style={
        {
          ...glow,
          ...PLATE_TYPE_VARS,
          '--lw': art?.lineWeight ?? 1,
          '--hit-k': HIT_K.lines,
          '--words-k': wordsFit(layout.frame),
        } as CSSProperties
      }
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="vehicle body hit location map"
      data-figure={`vehicle-${kind}`}
      data-states={dataStates}
      data-hits={hits.play?.burst.hits.map((h) => h.location).join(' ') || undefined}
      data-struck={struck ?? undefined}
      {...lens.handlers}
    >
      {art && <MemoFacings labels={labels} placed={layout} struck={facingStruck} />}
      {art && (
        <MemoBody
          u={u}
          art={art}
          callouts={callouts}
          layout={layout}
          state={state}
          play={hits.play}
          shownAt={hits.shownAt}
          guard={guard}
          {...interaction}
        />
      )}
      <MemoStructure wreck={wreck} systemsDown={state.disabled} {...structureAt(layout, art)} />
      {art && lens.at && <Lens u={u} at={lens.at} />}
    </svg>
  );
}

const NO_CALLOUTS: VehicleCallout[] = [];
const NO_LABELS: FacingLabel[] = [];

/**
 * The vehicle plate's callouts, facing labels and layout for its cell (plan plate-unboxed): each in its free spot,
 * the frame fitted round them (engine/plate/calloutLayout `vehicleLayout`).
 */
function useVehicleLayout(
  ref: RefObject<SVGSVGElement | null>,
  art: VehicleArt | null,
  target: Target,
  { fill, status }: { fill: boolean; status: boolean },
) {
  const cell = useCellSize(ref, fill);
  const { conditions, cover, armour } = target;
  const facing = effectiveVehicleFacing(target);
  const callouts = useMemo(
    () => (art ? vehicleCallouts(art, conditions || [], cover) : NO_CALLOUTS),
    [art, conditions, cover],
  );
  const labels = useMemo(() => (art ? vehicleFacings(art.view, armour, facing) : NO_LABELS), [art, armour, facing]);
  const layout = useMemo(
    () => (art ? vehicleLayout(art, callouts, labels, { cell, status }) : VEHICLE_UNPLACED),
    [art, callouts, labels, cell, status],
  );
  return { callouts, labels, layout };
}

interface BodyProps {
  u: string;
  art: VehicleArt;
  callouts: VehicleCallout[];
  /** Each callout's free spot, the words' scale and the frame (useVehicleLayout). */
  layout: CalloutLayout;
  state: PlateState;
  play: HitPlay | null;
  shownAt: number;
  /** What a hit may meet before it lands, and its size (Titanbane). */
  guard: Guard;
  targeting?: Targeting | null;
  onLocationContextMenu?: (id: string) => void;
}

/**
 * The vehicle's defs and its glow: its clips, conditions and hits, and - as on a person (plan plate-unboxed) - a
 * crippled part's snow and a destroyed part's hole in the glow.
 */
function VehicleDefs({
  u,
  art,
  callouts,
  state,
  play,
  regions,
}: Pick<BodyProps, 'u' | 'art' | 'callouts' | 'state' | 'play'> & {
  regions: ReturnType<typeof vehicleRegions>;
}) {
  const hurt = (kind: string) => callouts.filter((c) => damageOf(c.marks) === kind).map((c) => c.id);
  const holes = holesOf(regions, hurt('destroyed'));
  return (
    <>
      <ArtDefs u={u} art={art} glow>
        <LocationClips u={u} regions={regions} />
        <VehicleConditionDefs u={u} s={state} art={art} />
        {play && <HitDefs u={u} hits={play.burst.hits} />}
        {hurt('crippled').length > 0 && <SnowDefs u={u} />}
        <GlowHole u={u} regions={holes} />
      </ArtDefs>
      {!(state.dead || state.explodes) && (
        <g mask={holes.length ? `url(#${u}-glowhole)` : undefined}>
          <Glow u={u} />
        </g>
      )}
    </>
  );
}

/** The vehicle, its locations, the callouts and the sights - everything that needs the art. */
function VehicleBody(props: BodyProps) {
  const { u, art, callouts, layout, state, play, shownAt, guard, targeting, onLocationContextMenu } = props;
  const [hot, setHot] = useState<string | null>(null);
  const regions = vehicleRegions(art);
  const moving = !useHoldsStill();
  const locked = lockedOn(callouts, targeting ?? null);
  const { partBox } = vehicleFrame(art);
  return (
    <>
      <VehicleDefs {...{ u, art, callouts, state, play, regions }} />
      <g data-g="thrown">
        <g id={`${u}-figure`}>
          <VehicleLayers u={u} s={state} overlays={<VehicleOverlays u={u} s={state} />}>
            {callouts.map((c) => (
              <g key={c.id} {...vehicleLocationLayer(u, state, c.id)}>
                <Struck u={u} id={c.id} play={play} shownAt={shownAt} moving={moving} soak={guard.soak}>
                  {(hit) => (
                    <LocationLines
                      u={u}
                      art={art}
                      id={c.id}
                      className={clsx(marksClass(c.marks), hit, dimmedBy(locked, c.id))}
                      apart={state.explodes}
                    />
                  )}
                </Struck>
                {damageOf(c.marks) === 'crippled' && !state.explodes && <Snow u={u} clip={c.id} />}
              </g>
            ))}
          </VehicleLayers>
          <VehicleExtras s={state} art={art} />
        </g>
        {play && moving && <WaveBands u={u} />}
        {play && <StrikeMoments u={u} play={play} frame={vehicleFrame(art)} guard={guard} />}
      </g>
      {play && <BurstMarks u={u} play={play} frame={vehicleFrame(art)} moving={moving} />}
      <VehicleHits
        locations={hitOrder(callouts, regions)}
        regions={regions}
        boxOf={partBox}
        targeting={targeting}
        onHot={setHot}
        onContextMenu={onLocationContextMenu}
      />
      <VehicleWords callouts={callouts} layout={layout} art={art} hot={hot} />
    </>
  );
}

/** The vehicle's callouts at their free spots, and the readout of the place under the pointer above the hull. */
function VehicleWords({
  callouts,
  layout,
  art,
  hot,
}: {
  callouts: VehicleCallout[];
  layout: CalloutLayout;
  art: VehicleArt;
  hot: string | null;
}) {
  return (
    <>
      {callouts.map((c) => (
        <Callout key={c.id} c={c} at={layout.at[c.id]} k={layout.k} />
      ))}
      {hot && (
        <VehicleReadout
          c={callouts.find((c) => c.id === hot)}
          at={[layout.frame.x + layout.frame.w / 2, art.box[1] - 60]}
        />
      )}
    </>
  );
}

/** Each place's hit area: the live sights while a called shot is chosen, else plain areas that read out and take a
 * right-click. */
function VehicleHits(props: {
  locations: VehicleCallout[];
  regions: ReturnType<typeof vehicleRegions>;
  boxOf: (id: string) => readonly [number, number, number, number];
  targeting: Targeting | null | undefined;
  onHot: (id: string | null) => void;
  onContextMenu?: (id: string) => void;
}) {
  const { locations, regions, boxOf, targeting, onHot, onContextMenu } = props;
  if (targeting)
    return (
      <PlateSights
        locations={locations}
        boxOf={boxOf}
        hitOf={(id) => regions.hit[id]}
        targeting={targeting}
        onHot={onHot}
        onContextMenu={onContextMenu}
      />
    );
  return locations.map((c) => (
    <PlateHit key={c.id} id={c.id} d={regions.hit[c.id]} onHot={onHot} onContextMenu={onContextMenu} />
  ));
}

/**
 * The location under the pointer, in one line over the vehicle (plan design-gaps, P8; the person plate's readout):
 * its name and what it carries - no roll range (ruling 2026-10-09), its armour being the facings'.
 */
function VehicleReadout({ c, at: [x, y] }: { c: VehicleCallout | undefined; at: readonly [number, number] }) {
  if (!c) return null;
  return (
    <text className={styles.readout} x={x} y={y} textAnchor="middle" data-testid="plate-readout">
      {[c.name.toUpperCase(), ...c.tags.map((t) => t.text)].join(' · ')}
    </text>
  );
}

const TONE = {
  destroyed: styles.tagDestroyed,
  crippled: styles.tagCrippled,
  calm: styles.tagCalm,
  cover: styles.tagCover,
};

/**
 * A vehicle part's callout at its free spot: a short leader from the part's dot, its name, and its tags - no roll
 * range (ruling 2026-10-09).
 */
function Callout({ c, at: { x, row }, k }: { c: VehicleCallout; at: { x: number; row: number }; k: number }) {
  const right = c.side === 'R';
  const anchor = right ? 'start' : 'end';
  return (
    <g className={styles.callout} data-callout={c.id}>
      <CalloutLeader c={c} x={x} row={row} right={right} nameDy={-40} k={k} />
      <g transform={scaleAbout(x, row, k)}>
        {c.tags.map((t, i) => (
          <text key={t.text} className={clsx(styles.tag, TONE[t.tone])} x={x} y={row + 50 + 80 * i} textAnchor={anchor}>
            {t.text}
          </text>
        ))}
      </g>
    </g>
  );
}

interface FacingsProps {
  labels: FacingLabel[];
  /** Where the placement stood each label (on its own side, clear of the hull and the callouts), and the words' scale. */
  placed: CalloutLayout;
  struck: { side: VehicleSide; play: HitPlay } | null;
}

/**
 * Each facing's armour at that side of the vehicle; the facing an attack strikes is lit, and flares as a hit lands on
 * that side (keyed by the play, so the next one flares again).
 */
function Facings({ labels, placed, struck }: FacingsProps) {
  // The flare comes in the first landed hit's turn (glances go first), in its colour.
  const first = struck && firstLanded(struck.play.burst);
  const { k } = placed;
  return labels.map((f) => {
    const at = placed.labels[f.id];
    return (
      <text
        key={f.id === struck?.side ? `${f.id}:${struck.play.id}` : f.id}
        className={clsx(
          styles.facing,
          f.lit && styles.facingLit,
          f.id === struck?.side && first && flareClass(first.type),
        )}
        style={f.id === struck?.side && first ? { animationDelay: `${first.at}s` } : undefined}
        x={at.x}
        y={at.y}
        textAnchor={f.anchor}
        transform={scaleAbout(at.x, at.y, k)}
        data-facing={f.id}
      >
        {`${f.word} `}
        <tspan className={styles.facingArmour}>{f.armour}</tspan>
      </text>
    );
  });
}

interface LinesProps {
  u: string;
  art: VehicleArt;
  id: VehicleLocation;
  className: string;
  /** Exploded: each piece under its own clip, thrown by the blast (the Hull is one piece). */
  apart: boolean;
}

/** A location's lines - whole, or piece by piece once the vehicle has exploded. */
function LocationLines({ u, art, id, className, apart }: LinesProps) {
  if (!apart) return <PlateLines u={u} id={id} className={className} />;
  const pieces = piecesOf(art, id);
  return pieces.map((piece, k) => (
    <g key={k} className={fx.piece} style={throwStyle(art, id, piece)}>
      <PlateLines u={u} id={id} className={className} clip={piece === null ? id : `${id}-${k}`} />
    </g>
  ));
}

/** The words under the vehicle that its structure count cannot say: WRECK, or its systems down (ADR 2026-10-11). */
function Structure({ wreck, systemsDown, x, y }: { wreck: boolean; systemsDown: boolean; x: number; y: number }) {
  const label = wreck ? 'WRECK' : systemsDown ? 'SYSTEMS DOWN' : '';
  return <StatusLine label={label} at={[x, y]} testid="plate-structure" labelClass={styles.woundsLabel} />;
}

/**
 * Where the structure line stands: the frame's left, in the band under the drawing the placement kept for it. Two
 * numbers, not a fresh pair, so its memo holds across the lens's renders.
 */
const structureAt = (layout: CalloutLayout, art: VehicleArt | null) => ({
  x: layout.frame.x + 40,
  y: art ? vehicleStatus(art.box)[1] - 30 : 3500,
});

/** Where a vehicle's hits land: its anchors, each part's box (the Hull's, the whole drawing), its drawing's box. */
const vehicleFrame = (art: VehicleArt) => artFrame(art, (a) => a.box);

/** The knock away from the struck side, once (an Explosive hit or a critical), as a transform; none otherwise. */
function knockTransform(b: Burst, e: number, side: VehicleSide, view: 'top' | 'front') {
  const by = thrower(b);
  if (!by || e < by.at) return '';
  const k = vehicleThrowAt(e - by.at, by.amp, side, view);
  return `translate(${k.dx.toFixed(1)} ${k.dy.toFixed(1)})`;
}

/**
 * One frame of a struck vehicle: its hits' filters, the wave across it from the side struck, and the knock away from
 * that side. When the clock stops: back in place, no wave.
 */
function useVehicleHitFrame(hits: ReturnType<typeof usePlateHits>, art: VehicleArt | null, side: VehicleSide | null) {
  const writerOf = useSceneWriter();
  const onFrame = (t: number, root: SVGSVGElement) => {
    const burst = hits.play?.burst;
    if (!burst || !art || !side) return;
    const e = hits.elapsed(t);
    const first = firstLanded(burst);
    writeScene(writerOf(root), [
      ...hitNodes(burst.hits, e, false, HIT_K.filter),
      ...waveNodes(waveAt(first ? e - first.at : -1, side, art.view, art.box)),
      { key: 'thrown', attrs: { transform: knockTransform(burst, e, side, art.view) } },
    ]);
  };
  const onStop = (root: SVGSVGElement) =>
    applyScene(root, [
      { key: 'thrown', attrs: { transform: '' } },
      { key: 'wave0', attrs: { opacity: 0 } },
      { key: 'wave1', attrs: { opacity: 0 } },
    ]);
  return { onFrame, onStop };
}

/**
 * The side a burst struck, fixed when it arrives: the facing in play then (a facing changed mid-play must not flip the
 * wave or the knock). State adjusted during render, as useArrived does.
 */
function useStruckSide(play: HitPlay | null, facing: Parameters<typeof struckSide>[0]): VehicleSide | null {
  const [side, setSide] = useState<{ id: string; side: VehicleSide } | null>(null);
  if (play && side?.id !== play.id) setSide({ id: play.id, side: struckSide(facing, play.id) });
  return play && side?.id === play.id ? side.side : null;
}

/** Memoised, as the person plate's content is: the lens's pointer moves redraw the lens alone, not the vehicle. */
const MemoBody = memo(VehicleBody);
const MemoFacings = memo(Facings);
const MemoStructure = memo(Structure);

/** The facing a playing hit struck, with the play, held across renders (null with no hit or no side). */
function useFacingStruck(play: HitPlay | null, side: VehicleSide | null) {
  return useMemo(() => (play && side ? { side, play } : null), [play, side]);
}
